import React, { useEffect, useState, useRef } from "react";
import * as pdfjs from "pdfjs-dist";
import { motion } from "framer-motion";
import "./PDFCanvasViewer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const PDFCanvasViewer = ({ pdfUrl }) => {
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFullScreen, setIsFullScreen] = useState(false); // État pour savoir si en plein écran
  const canvasRef = useRef(null);
  const viewerRef = useRef(null);

  useEffect(() => {
    const loadPDF = async () => {
      try {
        const loadingTask = pdfjs.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        setNumPages(pdf.numPages);
        renderPage(pdf, currentPage);
      } catch (error) {
        console.error("❌ Erreur lors du chargement du PDF :", error);
      }
    };

    loadPDF();
  }, [pdfUrl, currentPage]);

  const renderPage = async (pdf, pageNum) => {
    try {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1.5 });
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d");

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      const renderContext = { canvasContext: context, viewport };
      page.render(renderContext);
    } catch (error) {
      console.error("❌ Erreur de rendu de la page :", error);
    }
  };

  const goNextPage = () => {
    if (currentPage < numPages) setCurrentPage(currentPage + 1);
  };

  const goPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const toggleFullScreen = () => {
    if (viewerRef.current) {
      if (!document.fullscreenElement) {
        viewerRef.current.requestFullscreen();
        setIsFullScreen(true); // Mettre l'état à true pour indiquer le plein écran
      } else {
        document.exitFullscreen();
        setIsFullScreen(false); // Mettre l'état à false quand on quitte le plein écran
      }
    }
  };

  // Fonction de téléchargement
  const downloadPDF = () => {
    const link = document.createElement("a");
    link.href = pdfUrl; // Utilise l'URL du PDF
    link.download = pdfUrl.split("/").pop(); // Télécharge le PDF avec le nom du fichier
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div 
      ref={viewerRef}
      className="pdf-canvas-container"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      style={{ 
        overflow: isFullScreen ? "auto" : "hidden", // Permet le scroll seulement en plein écran
        height: isFullScreen ? "100vh" : "auto"  // Ajuste la hauteur en fonction du mode plein écran
      }}
    >
      {/* Mode plein écran et téléchargement */}
      <div className="pdf-actions">
        <button className="fullscreen-btn" onClick={toggleFullScreen}>🔍 Plein écran</button>
        <button className="download-btn" onClick={downloadPDF}>📥 Télécharger</button>
      </div>

      {/* Contrôles de navigation */}
      <div className="pdf-controls">
        <button onClick={goPrevPage} disabled={currentPage === 1}>⬅️ Précédent</button>
        <span>Page {currentPage} / {numPages}</span>
        <button onClick={goNextPage} disabled={currentPage === numPages}>Suivant ➡️</button>
        
      </div>

      {/* Affichage du PDF */}
      <div className="pdf-pages">
        <motion.canvas
          ref={canvasRef}
          className="pdf-page"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        />
      </div>
    </motion.div>
  );
};

export default PDFCanvasViewer;
